// Plukkliste for ett skiplukk: én A4-side til den som leter fram skiene, med kunden, ønskene per
// par og tomme linjer for det som fylles ut for hånd (valgt ski, lengde, spenn, pris, plukket av).
// Brukes av «Skriv ut plukkliste» i OrderDetails og AktiveSkiplukk. plukklisterHtml setter flere
// sammen i ett dokument, én side hver. Alt fra brukere og databasen escapes (#30).
import { escapeHtml } from '../utils/format';
import { BRUK, NIVA } from './felles';

const tittel = (liste, verdi) => liste.find((x) => x.verdi === verdi)?.tittel || '';
const e = (v) => escapeHtml(v);

const verdi = (v) => (v ? e(v) : '<span class="tom">–</span>');

// To opplysninger per rad, så hvert par og kunden tar lite plass.
function rad2(e1, v1, e2, v2) {
  return `<tr><th>${e(e1)}</th><td>${verdi(v1)}</td><th>${e(e2)}</th><td>${verdi(v2)}</td></tr>`;
}

function parBlokk(p, i, antall) {
  const navn = antall > 1 ? `Par ${i + 1}` : 'Skiene';
  return `
    <section class="par">
      <h2>${e(navn)}${p.hvem ? ` – ${e(p.hvem)}` : ''}${p.grunnprepp ? ' <span class="merke">Grunnprepp</span>' : ''}</h2>
      <table class="info">
        ${rad2('Merke', p.merke, 'Produkt', p.produkt)}
        ${rad2('Bruk', tittel(BRUK, p.bruk), 'Nivå', tittel(NIVA, p.niva))}
        ${rad2('Vekt', p.vekt ? `${p.vekt} kg` : '', 'Høyde', p.hoyde ? `${p.hoyde} cm` : '')}
        ${rad2('Ønsket lengde', p.lengde, 'Budsjett', p.budsjett)}
      </table>
      <table class="fyll">
        <tr><th>Valgt ski</th><td colspan="3"></td></tr>
        <tr><th>Lengde / spenn</th><td></td><th>Pris</th><td></td></tr>
        <tr><th>Plukket av</th><td></td><th>Dato</th><td></td></tr>
      </table>
    </section>`;
}

const STIL = `<style>
  @page { size: A4; margin: 14mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; font-size: 11pt; margin: 0; }
  header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111; padding-bottom: 6pt; margin-bottom: 10pt; }
  h1 { font-size: 20pt; margin: 0; }
  .under { margin: 2pt 0 0; color: #333; }
  .nr { font-size: 22pt; font-weight: bold; text-align: right; }
  .meta { color: #333; text-align: right; font-size: 10pt; }
  table { width: 100%; border-collapse: collapse; }
  th, td { text-align: left; vertical-align: top; padding: 3pt 6pt; }
  th { width: 18%; font-weight: bold; }
  td { width: 32%; }
  .kunde { margin-bottom: 6pt; }
  .kunde th, .kunde td { padding: 2pt 6pt; }
  .beskjed { border: 1px solid #999; padding: 5pt 8pt; margin-bottom: 8pt; white-space: pre-line; min-height: 26pt; }
  .beskjed strong { display: block; margin-bottom: 2pt; }
  .par { border: 1.5px solid #111; border-radius: 4pt; padding: 5pt 8pt; margin-bottom: 6pt; break-inside: avoid; page-break-inside: avoid; }
  .par h2 { font-size: 12.5pt; margin: 0 0 3pt; }
  .merke { font-size: 9pt; border: 1px solid #111; border-radius: 8pt; padding: 0 5pt; vertical-align: middle; }
  .info th, .info td { padding: 1.5pt 6pt; border-bottom: 1px solid #ddd; }
  .fyll { margin-top: 4pt; }
  .fyll th { font-weight: normal; }
  .fyll td { border-bottom: 1px solid #111; height: 19pt; }
  .tom { color: #888; }
  .side + .side { break-before: page; page-break-before: always; }
  footer { margin-top: 10pt; font-size: 9pt; color: #444; border-top: 1px solid #999; padding-top: 4pt; }
</style>`;

function dokument(tittel, innhold) {
  return `<!doctype html>
<html lang="no">
<head>
<meta charset="utf-8">
<title>${e(tittel)}</title>
${STIL}
</head>
<body>
${innhold}
</body>
</html>`;
}

// ordre: lagret ordre (ordreid, Status, butikkid, RegistrertDato); felt: det som står i skjemaet nå.
function side({ ordre, kunde, par, ansvarlig, oppfolger, kommentar, butikk, utskrevet = new Date() }) {
  const kundeNavn = [kunde?.firstName, kunde?.lastName].filter(Boolean).join(' ');
  const dato = utskrevet.toLocaleString('nb-NO', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return `<div class="side">
  <header>
    <div>
      <h1>Plukkliste – skiplukk</h1>
      <p class="under">Kunden trenger hjelp til å finne riktige ski. Ikke en vareordre.</p>
    </div>
    <div>
      <div class="nr">Nr. ${e(ordre?.ordreid ?? '')}</div>
      <div class="meta">${e(butikk || '')}${butikk ? '<br>' : ''}Registrert ${e(ordre?.RegistrertDato || '')}</div>
    </div>
  </header>

  <table class="kunde">
    ${rad2('Kunde', kundeNavn, 'Ansvarlig', ansvarlig)}
    ${rad2('Telefon', kunde?.phoneNumber, 'Følges opp av', oppfolger)}
    ${rad2('E-post', kunde?.email, 'Antall par', String((par || []).length))}
  </table>

  <div class="beskjed"><strong>Beskjed</strong>${kommentar ? e(kommentar) : '<span class="tom">Ingen beskjed</span>'}</div>

  ${(par || []).map((p, i) => parBlokk(p, i, par.length)).join('')}

  <footer>Skrevet ut ${e(dato)}. Plukklisten inneholder personopplysninger (vekt og høyde): kast den i makuleringen når skiene er plukket.</footer>
</div>`;
}

export function plukklisteHtml(data) {
  return dokument(`Plukkliste – skiplukk ${data.ordre?.ordreid ?? ''}`, side(data));
}

// Flere skiplukk i ett utskriftsvindu (AktiveSkiplukk, «Skriv ut alle/valgte»), én side hver.
export function plukklisterHtml(liste) {
  const utskrevet = new Date();
  return dokument(`Plukklister – ${liste.length} skiplukk`, liste.map((d) => side({ ...d, utskrevet })).join('\n'));
}
